import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { EventAvailable } from "@mui/icons-material";
import type { VenueSlot } from "./types";
import DialogHeader from "./DialogHeader";
import {
  formatDate,
  formatTime,
  inferSlotLength,
  venueHirePaymentDeadlineNotice,
} from "./venueHireUtils";

export type ConfirmVenueBookingPayload = {
  first_name?: string;
  last_name?: string;
  voucher_code?: string;
};

interface ConfirmVenueBookingDialogProps {
  open: boolean;
  slot: VenueSlot | null;
  clubName: string;
  confirming: boolean;
  requiresName?: boolean;
  voucherError?: string | null;
  onClose: () => void;
  onConfirm: (payload?: ConfirmVenueBookingPayload) => void;
}

const ConfirmVenueBookingDialog: React.FC<ConfirmVenueBookingDialogProps> = ({
  open,
  slot,
  clubName,
  confirming,
  requiresName = false,
  voucherError = null,
  onClose,
  onConfirm,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");

  useEffect(() => {
    if (open) {
      setFirstName("");
      setLastName("");
      setVoucherOpen(false);
      setVoucherCode("");
    }
  }, [open, slot]);

  useEffect(() => {
    if (voucherError) {
      setVoucherOpen(true);
    }
  }, [voucherError]);

  if (!slot) return null;

  const slotLength =
    slot.slot_length ?? inferSlotLength(slot.start_time, slot.end_time);

  const canConfirm =
    !requiresName ||
    (firstName.trim().length > 0 && lastName.trim().length > 0);

  const handleConfirm = () => {
    const payload: ConfirmVenueBookingPayload = {};
    if (requiresName) {
      payload.first_name = firstName.trim();
      payload.last_name = lastName.trim();
    }
    const trimmedVoucher = voucherCode.trim();
    if (trimmedVoucher) {
      payload.voucher_code = trimmedVoucher;
    }
    onConfirm(Object.keys(payload).length > 0 ? payload : undefined);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !confirming && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogHeader
        title="Confirm booking"
        subtitle={clubName}
        icon={<EventAvailable color="primary" />}
        onClose={onClose}
        closeDisabled={confirming}
      />
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            You are about to book the following venue slot:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {formatDate(slot.date)}
          </Typography>
          <Typography variant="body1">
            {formatTime(slot.start_time)} – {formatTime(slot.end_time)} (
            {slotLength} min)
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            £{slot.price.toFixed(2)}
          </Typography>
          {requiresName && (
            <>
              <Typography variant="body2" color="text.secondary">
                Enter your name for this booking:
              </Typography>
              <TextField
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={confirming}
                fullWidth
                required
              />
              <TextField
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={confirming}
                fullWidth
                required
              />
            </>
          )}
          <Button
            size="small"
            onClick={() => setVoucherOpen((open) => !open)}
            disabled={confirming}
            sx={{ alignSelf: "flex-start", textTransform: "none", px: 0 }}
          >
            {voucherOpen ? "Hide voucher code" : "Have a voucher code?"}
          </Button>
          <Collapse in={voucherOpen}>
            <TextField
              label="Voucher code"
              value={voucherCode}
              onChange={(e) => setVoucherCode(e.target.value)}
              disabled={confirming}
              fullWidth
              error={Boolean(voucherError)}
              helperText={voucherError ?? undefined}
            />
          </Collapse>
          {voucherError && !voucherOpen && (
            <Alert severity="error">{voucherError}</Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            After confirming, you will receive bank transfer details to complete
            payment unless a valid voucher code waives the fee.{" "}
            {venueHirePaymentDeadlineNotice}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={confirming}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={confirming || !canConfirm}
          startIcon={
            confirming ? <CircularProgress size={18} color="inherit" /> : undefined
          }
        >
          {confirming ? "Booking…" : "Confirm booking"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmVenueBookingDialog;
